import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import toast from 'react-hot-toast'
import { AxiosError } from 'axios'
import Modal from 'components/modal'
import { Button, MultiSelectStoreDropdown, Switch } from 'components/common'
import { updateErpWindowByStores } from 'utils/api/inventoryApi'

const schema = yup
  .object()
  .shape({
    storeIds: yup
      .array()
      .of(yup.string().required())
      .min(1, 'Please select at least one store.')
      .required('Store is required field'),
    allowErpWindowUpdate: yup.boolean().required(),
  })
  .required()

type FormData = yup.InferType<typeof schema>

type Props = {
  isOpen: boolean
  closeModal: () => void
}

/**
 * One-time bulk action: sets Allow Erp Window Update for every barcode of the
 * selected stores. Runs on the background server; the outcome is recorded in
 * GenericLog (backend) and failures raise an ACTION_FAILED alert.
 */
const ErpWindowUpdateModal = ({ isOpen, closeModal }: Props) => {
  const [resetForm, setResetForm] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { storeIds: [], allowErpWindowUpdate: true },
  })

  const storeCount = watch('storeIds')?.length || 0
  const allowErpWindowUpdate = watch('allowErpWindowUpdate')

  const handleClose = () => {
    reset()
    setResetForm((pre) => !pre)
    closeModal()
  }

  const onSubmit = async (data: FormData) => {
    const actionToast = toast.loading('Starting ERP window update...')
    setSubmitting(true)

    try {
      await updateErpWindowByStores({
        storeIds: data.storeIds,
        allowErpWindowUpdate: data.allowErpWindowUpdate,
      })
      toast.success(
        'ERP window update started. Check the result in Logs > Generic Logs.',
        { id: actionToast, duration: 3000 },
      )
      handleClose()
    } catch (error) {
      const message =
        error instanceof AxiosError
          ? error?.response?.data?.errors?.msg
          : undefined
      toast.error(
        typeof message === 'string'
          ? message
          : 'Failed to start ERP window update.',
        { id: actionToast, duration: 3000 },
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      width={600}
      title="ERP Window Update by Stores"
      titleClassname="text-xl font-semibold"
      isOpen={isOpen}
      closeModal={handleClose}
      customStyle={{ padding: '30px 45px', height: 'auto' }}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-6">
        <MultiSelectStoreDropdown
          required
          isActive
          label="Stores"
          name="storeIds"
          setValue={setValue}
          resetForm={resetForm}
          error={errors?.storeIds?.message}
        />

        <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div>
            <p className="text-sm font-medium text-gray-800">
              Allow Erp Window Update
            </p>
            <p className="text-xs text-gray-500">
              {allowErpWindowUpdate
                ? 'Turns it ON for barcodes that have Block Erp Inventory on. Others are skipped.'
                : 'Turns it OFF for every barcode in the selected stores.'}
            </p>
          </div>
          <Controller
            name="allowErpWindowUpdate"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Switch checked={value} onChange={onChange} />
            )}
          />
        </div>

        {storeCount > 0 && (
          <p className="text-sm font-semibold text-[#DA2929]">
            This will turn {allowErpWindowUpdate ? 'ON' : 'OFF'} Allow Erp
            Window Update for all applicable barcodes in {storeCount} store(s).
          </p>
        )}

        <div className="flex w-full items-center gap-5">
          <Button
            className="w-full"
            buttonText="Submit"
            type="submit"
            disabled={submitting}
          />
          <Button
            className="w-full"
            buttonText="Cancel"
            variant="outline"
            type="button"
            onClick={handleClose}
          />
        </div>
      </form>
    </Modal>
  )
}

export default ErpWindowUpdateModal
